// =====================================================
// SITES ADMIN PAGE - Centros Médicos
// Gestión de centros con asignación de especialidades
// =====================================================

import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Building2,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Edit2,
  Power,
  Loader2,
  Save,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Phone,
  Mail,
  Percent,
  Stethoscope,
  Layers,
} from 'lucide-react';
import { sitesApi, type Site, type SiteLinkedSpecialty } from '../../api/sites';
import { LogoUploader } from '../../components/ui/LogoUploader';
import { societiesApi } from '../../api/societies';
import type { SocietySelect } from '../../types/catalog-admin';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { toast } from '../../components/ui/Toast';

type SortField = 'name' | 'code' | 'createdAt';

interface SiteFormData {
  code: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  agreementPercent: number;
  defaultSocietyId: string;
  paymentConditions: string;
}

export function SitesAdminPage() {
  // Data state
  const [sites, setSites] = useState<Site[]>([]);
  const [societies, setSocieties] = useState<SocietySelect[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<any>(null);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Modal state - Crear/Editar
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLogoUrl, setModalLogoUrl] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<Site | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Modal state - Especialidades
  const [isSpecialtiesModalOpen, setIsSpecialtiesModalOpen] = useState(false);
  const [specialtiesItem, setSpecialtiesItem] = useState<Site | null>(null);
  const [linkedSpecialties, setLinkedSpecialties] = useState<SiteLinkedSpecialty[]>([]);
  const [selectedSpecialtyIds, setSelectedSpecialtyIds] = useState<string[]>([]);
  const [selectedSubspecialtyIds, setSelectedSubspecialtyIds] = useState<string[]>([]);
  const [isLoadingSpecialties, setIsLoadingSpecialties] = useState(false);
  const [isSavingSpecialties, setIsSavingSpecialties] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showSelectAllConfirm, setShowSelectAllConfirm] = useState(false);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    item: Site | null;
    action: 'activate' | 'deactivate';
  }>({ isOpen: false, item: null, action: 'deactivate' });
  const [isProcessing, setIsProcessing] = useState(false);

  // Form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SiteFormData>({
    defaultValues: {
      code: '',
      name: '',
      address: '',
      phone: '',
      email: '',
      agreementPercent: 0,
      defaultSocietyId: '',
      paymentConditions: '',
    },
  });

  // Load data
  const loadSocieties = useCallback(async () => {
    try {
      const data = await societiesApi.getForSelect();
      setSocieties(data);
    } catch (err) {
      console.error('Error loading societies:', err);
    }
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await sitesApi.getAll({
        page,
        limit,
        search: searchQuery || undefined,
        sortBy,
        sortOrder,
        includeInactive: showInactive,
      });
      setSites(result.data);
      setMeta(result.meta);
    } catch (err: any) {
      setError(err.message || 'Error al cargar centros');
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, searchQuery, sortBy, sortOrder, showInactive]);

  useEffect(() => {
    loadSocieties();
  }, [loadSocieties]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormError(null);
    setModalLogoUrl(null);
    reset({
      code: '',
      name: '',
      address: '',
      phone: '',
      email: '',
      agreementPercent: 0,
      defaultSocietyId: '',
      paymentConditions: '',
    });
    setIsModalOpen(true);
  };

  const handleEdit = (item: Site) => {
    setEditingItem(item);
    setFormError(null);
    setModalLogoUrl(item.logoUrl || null);
    reset({
      code: item.code,
      name: item.name,
      address: item.address || '',
      phone: item.phone || '',
      email: item.email || '',
      agreementPercent: item.agreementPercent,
      defaultSocietyId: item.defaultSocietyId || '',
      paymentConditions: (item as any).paymentConditions || '',
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
    }
  };

  const handleSave = async (formData: SiteFormData) => {
    setFormError(null);
    setIsSaving(true);

    try {
      const payload = {
        code: formData.code,
        name: formData.name,
        address: formData.address || undefined,
        phone: formData.phone || undefined,
        email: formData.email || undefined,
        agreementPercent: formData.agreementPercent || 0,
        defaultSocietyId: formData.defaultSocietyId || undefined,
        paymentConditions: formData.paymentConditions || undefined,
        logoUrl: modalLogoUrl !== null ? modalLogoUrl : undefined,
      };

      if (editingItem) {
        await sitesApi.update(editingItem.id, payload);
        toast.success('Actualizado', `Centro "${formData.name}" actualizado`);
      } else {
        await sitesApi.create(payload);
        toast.success('Creado', `Centro "${formData.name}" creado`);
      }
      setIsModalOpen(false);
      setEditingItem(null);
      loadData();
    } catch (err: any) {
      setFormError(err.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = (item: Site) => {
    setConfirmDialog({
      isOpen: true,
      item,
      action: item.isActive ? 'deactivate' : 'activate',
    });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.item) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await sitesApi.deactivate(confirmDialog.item.id);
        toast.success('Desactivado', `Centro "${confirmDialog.item.name}" desactivado`);
      } else {
        await sitesApi.reactivate(confirmDialog.item.id);
        toast.success('Activado', `Centro "${confirmDialog.item.name}" activado`);
      }
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo cambiar el estado');
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' });
    }
  };

  // =====================================================
  // MODAL: ESPECIALIDADES
  // =====================================================
  const handleOpenSpecialtiesModal = async (item: Site) => {
    setSpecialtiesItem(item);
    setIsSpecialtiesModalOpen(true);
    setIsLoadingSpecialties(true);

    try {
      const data = await sitesApi.getLinkedSpecialties(item.id);
      setLinkedSpecialties(data);

      // Establecer selecciones actuales
      const specIds = data.filter((s) => s.isLinked).map((s) => s.id);
      const subIds = data
        .flatMap((s) => s.subspecialties)
        .filter((sub) => sub.isLinked)
        .map((sub) => sub.id);

      setSelectedSpecialtyIds(specIds);
      setSelectedSubspecialtyIds(subIds);
    } catch (err: any) {
      toast.error('Error', 'No se pudieron cargar las especialidades');
      setIsSpecialtiesModalOpen(false);
    } finally {
      setIsLoadingSpecialties(false);
    }
  };

  const handleCloseSpecialtiesModal = () => {
    if (!isSavingSpecialties) {
      setIsSpecialtiesModalOpen(false);
      setSpecialtiesItem(null);
      setLinkedSpecialties([]);
      setSelectedSpecialtyIds([]);
      setSelectedSubspecialtyIds([]);
      setShowClearConfirm(false);
      setShowSelectAllConfirm(false);
    }
  };

  const handleToggleSpecialty = (specId: string) => {
    const isSelected = selectedSpecialtyIds.includes(specId);

    if (isSelected) {
      // Al deseleccionar, quitar también las subespecialidades
      setSelectedSpecialtyIds((prev) => prev.filter((id) => id !== specId));
      const spec = linkedSpecialties.find((s) => s.id === specId);
      if (spec) {
        const subIds = spec.subspecialties.map((sub) => sub.id);
        setSelectedSubspecialtyIds((prev) => prev.filter((id) => !subIds.includes(id)));
      }
    } else {
      setSelectedSpecialtyIds((prev) => [...prev, specId]);
    }
  };

  const handleToggleSubspecialty = (subId: string) => {
    setSelectedSubspecialtyIds((prev) =>
      prev.includes(subId) ? prev.filter((id) => id !== subId) : [...prev, subId]
    );
  };

  const handleToggleAllSubspecialties = (specId: string) => {
    const spec = linkedSpecialties.find((s) => s.id === specId);
    if (!spec) return;

    const subIds = spec.subspecialties.map((sub) => sub.id);
    const allSelected = subIds.every((id) => selectedSubspecialtyIds.includes(id));

    if (allSelected) {
      setSelectedSubspecialtyIds((prev) => prev.filter((id) => !subIds.includes(id)));
    } else {
      setSelectedSubspecialtyIds((prev) => [...new Set([...prev, ...subIds])]);
    }
  };

  const handleClearSpecialties = () => {
    setSelectedSpecialtyIds([]);
    setSelectedSubspecialtyIds([]);
    setShowClearConfirm(false);
  };

  const handleSelectAllSpecialties = () => {
    // Seleccionar todas las especialidades
    const allSpecIds = linkedSpecialties.map((spec) => spec.id);
    setSelectedSpecialtyIds(allSpecIds);

    // Seleccionar todas las subespecialidades
    const allSubIds = linkedSpecialties.flatMap((spec) =>
      spec.subspecialties.map((sub) => sub.id)
    );
    setSelectedSubspecialtyIds(allSubIds);
    setShowSelectAllConfirm(false);
  };

  const handleSaveSpecialties = async () => {
    if (!specialtiesItem) return;
    setIsSavingSpecialties(true);
    try {
      await sitesApi.updateSpecialties(specialtiesItem.id, {
        specialtyIds: selectedSpecialtyIds,
        subspecialtyIds: selectedSubspecialtyIds,
      });
      toast.success('Guardado', 'Especialidades actualizadas');
      setIsSpecialtiesModalOpen(false);
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo guardar');
    } finally {
      setIsSavingSpecialties(false);
    }
  };

  // Helpers
  const clearAllFilters = () => {
    setShowInactive(false);
    setSearchQuery('');
    setPage(1);
  };

  const hasActiveFilters = showInactive || searchQuery;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Centros</h1>
          <p className="text-sm text-gray-500 mt-1">Gestión de centros médicos</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center flex-1 max-w-2xl gap-2">
            <form onSubmit={handleSearch} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por código o nombre..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </form>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters
                  ? 'bg-blue-50 border-blue-200 text-blue-600'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filtros
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-100 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nuevo centro
            </button>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-2">
              Filtros activos:
            </span>
            {showInactive && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Incluye inactivos
                <button
                  onClick={() => {
                    setShowInactive(false);
                    setPage(1);
                  }}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            {searchQuery && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Búsqueda: "{searchQuery}"
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setPage(1);
                  }}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            <button
              onClick={clearAllFilters}
              className="text-xs text-gray-400 hover:text-red-500 font-medium ml-2 underline underline-offset-2"
            >
              Limpiar todo
            </button>
          </div>
        )}

        {showFilters && (
          <div className="flex flex-col sm:flex-row sm:items-end justify-between border-t border-gray-100 pt-3 gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <label className="inline-flex items-center cursor-pointer group pb-1">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => {
                    setShowInactive(e.target.checked);
                    setPage(1);
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900">
                  Mostrar inactivos
                </span>
              </label>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ordenar:</span>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field as SortField);
                    setSortOrder(order as 'asc' | 'desc');
                    setPage(1);
                  }}
                  className="text-sm border-none bg-transparent py-0 pl-0 pr-8 font-medium text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600"
                >
                  <option value="name-asc">Nombre (A-Z)</option>
                  <option value="name-desc">Nombre (Z-A)</option>
                  <option value="code-asc">Código (A-Z)</option>
                  <option value="code-desc">Código (Z-A)</option>
                  <option value="createdAt-desc">Más reciente</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ver:</span>
                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                  {[10, 25, 50].map((n) => (
                    <button
                      key={n}
                      onClick={() => {
                        setLimit(n);
                        setPage(1);
                      }}
                      className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 last:border-r-0 transition-colors ${
                        limit === n
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {sites.length === 0 && !isLoading ? (
          <div className="p-12 text-center">
            <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {hasActiveFilters ? 'Sin resultados' : 'No hay centros'}
            </h3>
            <p className="text-gray-500">
              {hasActiveFilters
                ? 'No se encontraron centros con ese criterio'
                : 'Crea tu primer centro médico'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      Código
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      Nombre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                      Contacto
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                      Especialidades
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                      Convenio
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sites.map((item) => (
                    <tr
                      key={item.id}
                      className={`hover:bg-gray-50 ${!item.isActive ? 'bg-gray-50/50' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-lg">
                          {item.code}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <span
                            className={`font-medium ${
                              item.isActive ? 'text-gray-900' : 'text-gray-500'
                            }`}
                          >
                            {item.name}
                          </span>
                          {item.address && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                              <MapPin className="h-3 w-3" />
                              <span className="truncate max-w-xs">{item.address}</span>
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {item.phone && (
                            <p className="text-xs text-gray-600 flex items-center gap-1">
                              <Phone className="h-3 w-3 text-gray-400" />
                              {item.phone}
                            </p>
                          )}
                          {item.email && (
                            <p className="text-xs text-gray-600 flex items-center gap-1">
                              <Mail className="h-3 w-3 text-gray-400" />
                              {item.email}
                            </p>
                          )}
                          {!item.phone && !item.email && (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {item.specialtiesCount > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 text-xs font-medium rounded-full">
                            <Stethoscope className="h-3 w-3" />
                            {item.specialtiesCount}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Sin asignar</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {item.agreementPercent > 0 ? (
                          <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700">
                            {item.agreementPercent}%
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleToggleStatus(item)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                            item.isActive ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                          role="switch"
                          aria-checked={item.isActive}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                              item.isActive ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          >
                            <Power
                              className={`h-3 w-3 absolute top-1 left-1 ${
                                item.isActive ? 'text-blue-600' : 'text-gray-400'
                              }`}
                            />
                          </span>
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleOpenSpecialtiesModal(item)}
                            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                            title="Asignar especialidades"
                          >
                            <Stethoscope className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                            title="Editar"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {isLoading && (
              <div className="flex items-center justify-center py-8 border-t">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            )}

            {meta && meta.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Mostrando {(meta.page - 1) * meta.limit + 1} a{' '}
                  {Math.min(meta.page * meta.limit, meta.total)} de {meta.total}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(meta.page - 1)}
                    disabled={!meta.hasPrevPage || isLoading}
                    className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700">
                    Página {meta.page} de {meta.totalPages}
                  </span>
                  <button
                    onClick={() => setPage(meta.page + 1)}
                    disabled={!meta.hasNextPage || isLoading}
                    className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* MODAL: CREAR/EDITAR CENTRO */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingItem ? 'Editar centro' : 'Nuevo centro'}
        subtitle={editingItem ? `Editando: ${editingItem.name}` : 'Completa los datos del centro'}
      >
        <form onSubmit={handleSubmit(handleSave)}>
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="h-4 w-4 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-600 mt-0.5">{formError}</p>
              </div>
              <button
                type="button"
                onClick={() => setFormError(null)}
                className="flex-shrink-0 p-1 text-red-400 hover:text-red-600 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código *
                </label>
                <input
                  type="text"
                  {...register('code', {
                    required: 'El código es obligatorio',
                    maxLength: { value: 10, message: 'Máximo 10 caracteres' },
                  })}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm uppercase ${
                    errors.code ? 'border-red-300 bg-red-50/50' : 'border-gray-200'
                  }`}
                  placeholder="SITE01"
                />
                {errors.code && (
                  <p className="mt-1 text-xs text-red-500">{errors.code.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  {...register('name', { required: 'El nombre es obligatorio' })}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm ${
                    errors.name ? 'border-red-300' : 'border-gray-200'
                  }`}
                  placeholder="Nombre del centro"
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dirección
              </label>
              <input
                type="text"
                {...register('address')}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                placeholder="Dirección completa"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <input
                  type="tel"
                  {...register('phone')}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                  placeholder="+34 XXX XXX XXX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  {...register('email')}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                  placeholder="email@centro.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Percent className="h-4 w-4 inline mr-1" />
                  Porcentaje convenio
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  {...register('agreementPercent', { valueAsNumber: true })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sociedad por defecto
                </label>
                <select
                  {...register('defaultSocietyId')}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                >
                  <option value="">Sin asignar</option>
                  {societies.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.code} - {s.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Condiciones de pago
                <span className="ml-1 text-xs text-gray-400 font-normal">(aparece en el pie del PDF)</span>
              </label>
              <textarea
                {...register('paymentConditions')}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none"
                placeholder="Ej: El pago deberá realizarse en un plazo de 30 días..."
              />

            <LogoUploader
              currentLogoUrl={modalLogoUrl}
              label="Logo del centro colaborador"
              hint="Aparece en la esquina superior derecha del PDF (opcional)"
              onChange={(url) => setModalLogoUrl(url)}
            />
            </div>
          </div>

          <ModalFooter>
            <button
              type="button"
              onClick={handleCloseModal}
              disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {editingItem ? 'Guardar' : 'Crear'}
                </>
              )}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* MODAL: ESPECIALIDADES */}
      <Modal
        isOpen={isSpecialtiesModalOpen}
        onClose={handleCloseSpecialtiesModal}
        title="Asignar especialidades"
        subtitle={specialtiesItem ? `${specialtiesItem.code} - ${specialtiesItem.name}` : ''}
      >
        {isLoadingSpecialties ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Header informativo */}
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-xl border border-purple-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Stethoscope className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-purple-900">
                    Especialidades y subespecialidades
                  </p>
                  <p className="text-xs text-purple-600">
                    Marca las casillas para asignar al centro
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowSelectAllConfirm(true)}
                  className="text-xs text-purple-600 hover:text-purple-800 px-2 py-1 hover:bg-white rounded transition-colors"
                >
                  Seleccionar todo
                </button>
                {selectedSpecialtyIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowClearConfirm(true)}
                    className="text-xs text-purple-600 hover:text-red-500 px-2 py-1 hover:bg-white rounded transition-colors"
                  >
                    Limpiar todo
                  </button>
                )}
              </div>
            </div>

            {/* Lista de especialidades */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="max-h-[350px] overflow-y-auto divide-y divide-gray-100">
                {linkedSpecialties.map((spec) => {
                  const isSelected = selectedSpecialtyIds.includes(spec.id);
                  const selectedSubCount = spec.subspecialties.filter((sub) =>
                    selectedSubspecialtyIds.includes(sub.id)
                  ).length;
                  const allSubsSelected =
                    spec.subspecialties.length > 0 &&
                    spec.subspecialties.every((sub) =>
                      selectedSubspecialtyIds.includes(sub.id)
                    );

                  return (
                    <div
                      key={spec.id}
                      className={isSelected ? 'bg-purple-50/50' : 'bg-white'}
                    >
                      {/* Fila de especialidad */}
                      <label className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSpecialty(spec.id)}
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span
                          className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded"
                          title={spec.code}
                        >
                          {spec.code}
                        </span>
                        <span
                          className="flex-1 text-sm text-gray-900"
                          title={spec.name}
                        >
                          {spec.name}
                        </span>
                        {spec.subspecialties.length > 0 && (
                          <span className="text-xs text-gray-400">
                            {isSelected && selectedSubCount > 0 ? (
                              <span className="text-purple-600 font-medium">
                                {selectedSubCount}/{spec.subspecialties.length} sub.
                              </span>
                            ) : (
                              <span>{spec.subspecialties.length} sub.</span>
                            )}
                          </span>
                        )}
                      </label>

                      {/* Subespecialidades indentadas */}
                      {isSelected && spec.subspecialties.length > 0 && (
                        <div className="pl-11 pr-4 pb-3 space-y-1">
                          <div className="flex items-center justify-between py-1 mb-1">
                            <span className="text-[10px] font-medium text-purple-600 uppercase tracking-wider flex items-center gap-1">
                              <Layers className="h-3 w-3" />
                              Subespecialidades
                            </span>
                            <button
                              type="button"
                              onClick={() => handleToggleAllSubspecialties(spec.id)}
                              className="text-[10px] font-medium text-purple-600 hover:text-purple-800 hover:underline"
                            >
                              {allSubsSelected ? 'Quitar todas' : 'Seleccionar todas'}
                            </button>
                          </div>

                          {spec.subspecialties.map((sub) => {
                            const isSubSelected = selectedSubspecialtyIds.includes(sub.id);
                            return (
                              <label
                                key={sub.id}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                                  isSubSelected
                                    ? 'bg-purple-100 border border-purple-200'
                                    : 'bg-white border border-gray-200 hover:border-purple-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSubSelected}
                                  onChange={() => handleToggleSubspecialty(sub.id)}
                                  className="h-3.5 w-3.5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                />
                                <span
                                  className={`text-sm ${
                                    isSubSelected
                                      ? 'text-purple-900 font-medium'
                                      : 'text-gray-700'
                                  }`}
                                >
                                  {sub.name}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Resumen */}
            {selectedSpecialtyIds.length > 0 && (
              <div className="text-xs text-gray-500 px-1">
                <span className="font-medium text-gray-700">
                  {selectedSpecialtyIds.length}
                </span>{' '}
                especialidad{selectedSpecialtyIds.length !== 1 ? 'es' : ''}
                {selectedSubspecialtyIds.length > 0 && (
                  <>
                    {' '}
                    y{' '}
                    <span className="font-medium text-purple-600">
                      {selectedSubspecialtyIds.length}
                    </span>{' '}
                    subespecialidad{selectedSubspecialtyIds.length !== 1 ? 'es' : ''}
                  </>
                )}{' '}
                seleccionadas
              </div>
            )}
          </div>
        )}

        <ModalFooter>
          <button
            type="button"
            onClick={handleCloseSpecialtiesModal}
            disabled={isSavingSpecialties}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Cancelar
          </button>
          <button
            onClick={handleSaveSpecialties}
            disabled={isSavingSpecialties || isLoadingSpecialties}
            className="px-4 py-2.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-xl disabled:opacity-50 flex items-center gap-2"
          >
            {isSavingSpecialties ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Guardar
              </>
            )}
          </button>
        </ModalFooter>
      </Modal>

      {/* Confirmación para limpiar especialidades */}
      <ConfirmDialog
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={handleClearSpecialties}
        variant="warning"
        title="¿Limpiar selección?"
        message={`Se quitarán ${selectedSpecialtyIds.length} especialidad${
          selectedSpecialtyIds.length !== 1 ? 'es' : ''
        }${
          selectedSubspecialtyIds.length > 0
            ? ` y ${selectedSubspecialtyIds.length} subespecialidad${
                selectedSubspecialtyIds.length !== 1 ? 'es' : ''
              }`
            : ''
        } seleccionadas.`}
        confirmText="Limpiar"
      />

      {/* Confirmación para seleccionar todo */}
      <ConfirmDialog
        isOpen={showSelectAllConfirm}
        onClose={() => setShowSelectAllConfirm(false)}
        onConfirm={handleSelectAllSpecialties}
        variant="info"
        title="¿Seleccionar todo?"
        message={`Se seleccionarán ${linkedSpecialties.length} especialidad${
          linkedSpecialties.length !== 1 ? 'es' : ''
        } y todas sus subespecialidades.`}
        confirmText="Seleccionar todo"
      />

      {/* Confirm Dialog para activar/desactivar */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() =>
          !isProcessing &&
          setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' })
        }
        onConfirm={handleConfirmToggle}
        isLoading={isProcessing}
        variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        title={
          confirmDialog.action === 'deactivate'
            ? '¿Desactivar centro?'
            : '¿Activar centro?'
        }
        message={
          confirmDialog.action === 'deactivate'
            ? `El centro "${confirmDialog.item?.name}" será desactivado.`
            : `El centro "${confirmDialog.item?.name}" será reactivado.`
        }
        confirmText={confirmDialog.action === 'deactivate' ? 'Desactivar' : 'Activar'}
      />
    </div>
  );
}

export default SitesAdminPage;
